import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { PackageSearch, PackageX, X } from "lucide-react";
import {
  api,
  fmtMoney,
  type Product,
  type WriteOff,
  type WriteOffReason,
} from "../api";
import { fmtDateTime } from "../lib/format";
import { useBranch } from "../lib/use-branch";
import { usePageHeader } from "../lib/use-page-header";
import { BranchLabel } from "../components/branch-label";
import { PageFilterPopover } from "../components/page-filter-popover";
import { ProductCombobox } from "../components/product-combobox";
import { QtyStepper } from "../components/qty-stepper";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Money, Mono, StatusChip } from "../components/ui/ledger";
import { Pagination } from "../components/ui/pagination";
import { Select } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const PAGE_SIZE = 30;

export const WRITE_OFF_REASONS: WriteOffReason[] = [
  "singan",
  "muddati_otgan",
  "yoqolgan",
  "ichki_istemol",
  "inventarizatsiya",
];

// Sabab → chip ohangi. "Inventarizatsiya" — texnik tuzatish, shuning uchun
// neytral; yo'qolish/o'g'irlik eng jiddiy signal.
const REASON_TONE: Record<WriteOffReason, "warn" | "danger" | "muted"> = {
  singan: "warn",
  muddati_otgan: "warn",
  yoqolgan: "danger",
  ichki_istemol: "muted",
  inventarizatsiya: "muted",
};

function ReasonChip({ sabab, t }: { sabab: WriteOffReason; t: TFunction }) {
  return (
    <StatusChip tone={REASON_TONE[sabab]}>
      {t(`chiqim.sabab.${sabab}`)}
    </StatusChip>
  );
}

/**
 * Hisobdan chiqarish (spisaniye) — buzilgan / muddati o'tgan / yo'qolgan /
 * ustaxona o'zi ishlatgan tovarni sabab bilan ombordan chiqarish.
 *
 * Sotuvlar/xaridlar kabi sana ustunli daftar EMAS, qaytarishlar kabi oddiy
 * sahifalangan jadval: bu hujjatda kontragent ham, to'lov ham yo'q, ya'ni
 * `LedgerDayTable`ning "jami / to'langan / qarz" ustunlari bo'sh qolardi.
 *
 * Hujjat O'ZGARMAS — tahrirlash/o'chirish tugmasi yo'q. Xato kiritilgani
 * tovar kartochkasidagi "Tuzatish" bilan kompensatsiya qilinadi, shunda
 * jurnalda ikkala hodisa ham qoladi.
 *
 * Bu — Ombor sahifasining tabi, shuning uchun `tovars` qorovuli takrorlanmaydi.
 */
export function ChiqimPage({
  openSignal,
  initialProduct,
  focusDoc,
}: {
  /** Tablar qatoridagi "Yangi chiqim" tugmasi (OmborPage) signal orqali ochadi. */
  openSignal?: number;
  /** Signallar tabidan kelgan tovar — forma o'sha qator bilan ochiladi. */
  initialProduct?: { product: Product; seq: number } | null;
  /** Tovar jurnalidan kelingan hujjat — detal oynasi darhol ochiladi. */
  focusDoc?: { id: number; seq: number } | null;
} = {}) {
  const { t } = useTranslation(["ombor", "common"]);
  const { selectedBranchId, showSelector } = useBranch();
  const [items, setItems] = useState<WriteOff[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sabab, setSabab] = useState<"" | WriteOffReason>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .writeOffs({
        sabab: sabab || undefined,
        branch_id: selectedBranchId ?? undefined,
        date_from: from || undefined,
        // Sana kiritish "kun" ma'nosida — kun oxirigacha qamrab olamiz.
        date_to: to ? `${to}T23:59:59` : undefined,
        page,
        page_size: PAGE_SIZE,
      })
      .then((pg) => {
        if (!alive) return;
        setItems(pg.items);
        setTotal(pg.total);
        setErr(null);
      })
      .catch((e) => {
        if (!alive) return;
        setErr((e as { message?: string }).message || t("common:common.error"));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [sabab, from, to, page, selectedBranchId, refresh, t]);

  // Filial almashsa ro'yxat birinchi sahifadan quriladi — ikki filialning
  // hujjatlari aralashib ketmasin.
  useEffect(() => {
    setPage(1);
  }, [selectedBranchId]);

  useEffect(() => {
    if (openSignal) setCreating(true);
  }, [openSignal]);

  // Signallar tabidan kelingan tovar. `seq` — bir xil tovar qayta bosilsa ham
  // forma yana ochilsin (`focusDoc` dagi bilan bir xil sabab).
  useEffect(() => {
    if (initialProduct) setCreating(true);
  }, [initialProduct?.product.id, initialProduct?.seq]);

  useEffect(() => {
    if (focusDoc) setDetailId(focusDoc.id);
    // `seq` ham bog'liqlikda — bir xil hujjat qayta bosilsa oyna yana ochilsin.
  }, [focusDoc?.id, focusDoc?.seq]);

  const detail = useMemo(
    () => items.find((w) => w.id === detailId) ?? null,
    [items, detailId],
  );
  // Jurnaldan kelingan hujjat joriy sahifada bo'lmasligi mumkin — alohida olamiz.
  const [fetched, setFetched] = useState<WriteOff | null>(null);
  useEffect(() => {
    if (detailId === null || detail !== null) {
      setFetched(null);
      return;
    }
    let alive = true;
    api
      .getWriteOff(detailId)
      .then((w) => alive && setFetched(w))
      .catch(() => alive && setDetailId(null));
    return () => {
      alive = false;
    };
  }, [detailId, detail]);
  const shown = detail ?? fetched;

  const filterCount = (sabab ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0);
  const clearFilters = () => {
    setSabab("");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const filterNode = useMemo(
    () => (
      <PageFilterPopover count={filterCount} onClear={clearFilters}>
        <div className="space-y-1.5">
          <Label htmlFor="wo-sabab">{t("chiqim.filters.sabab")}</Label>
          <Select
            id="wo-sabab"
            value={sabab}
            onChange={(e) => {
              setSabab(e.target.value as "" | WriteOffReason);
              setPage(1);
            }}
          >
            <option value="">{t("chiqim.filters.all")}</option>
            {WRITE_OFF_REASONS.map((s) => (
              <option key={s} value={s}>
                {t(`chiqim.sabab.${s}`)}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wo-from">{t("chiqim.filters.from")}</Label>
          <Input
            id="wo-from"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wo-to">{t("chiqim.filters.to")}</Label>
          <Input
            id="wo-to"
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </PageFilterPopover>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sabab, from, to, filterCount, t],
  );

  usePageHeader({ filters: filterNode, filterCount });

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<PackageX />}
              title={t("chiqim.empty.title")}
              description={t("chiqim.empty.desc")}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("chiqim.table.sana")}</TableHead>
                  <TableHead>{t("chiqim.table.sabab")}</TableHead>
                  <TableHead>{t("chiqim.table.tovarlar")}</TableHead>
                  <TableHead className="text-right">
                    {t("chiqim.table.qiymat")}
                  </TableHead>
                  {showSelector && (
                    <TableHead>{t("chiqim.table.filial")}</TableHead>
                  )}
                  <TableHead>{t("chiqim.table.izoh")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((w) => (
                  <TableRow
                    key={w.id}
                    className="cursor-pointer"
                    onClick={() => setDetailId(w.id)}
                  >
                    <TableCell className="whitespace-nowrap">
                      <Mono className="text-muted-foreground">
                        {fmtDateTime(w.created_at)}
                      </Mono>
                    </TableCell>
                    <TableCell>
                      <ReasonChip sabab={w.sabab} t={t} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {t("chiqim.itemsCount", { count: w.lines.length })}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Xodimda `null` → fmtMoney "—" chizadi. */}
                      <Money tone={w.jami_tannarx ? "neg" : "default"}>
                        {fmtMoney(w.jami_tannarx)}
                      </Money>
                    </TableCell>
                    {showSelector && (
                      <TableCell>
                        <BranchLabel
                          name={w.branch_name}
                          isActive={w.branch_is_active}
                        />
                      </TableCell>
                    )}
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {w.izoh || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        {!loading && items.length > 0 && (
          <Pagination
            page={page}
            total={total}
            pageSize={PAGE_SIZE}
            onPage={setPage}
          />
        )}
      </Card>

      {creating && (
        <Dialog
          open
          onOpenChange={(o) => !o && setCreating(false)}
          contentClassName="max-w-2xl p-0"
        >
          <ChiqimForm
            initialProduct={initialProduct?.product ?? null}
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              setPage(1);
              setRefresh((x) => x + 1);
            }}
          />
        </Dialog>
      )}

      {detailId !== null && (
        <Dialog
          open
          onOpenChange={(o) => !o && setDetailId(null)}
          contentClassName="max-w-lg"
        >
          <div className="space-y-4 p-5">
            {shown === null ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : (
              <>
                <div>
                  <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
                    {t("chiqim.detail.title", { id: shown.id })}
                  </h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {fmtDateTime(shown.created_at)}
                    {shown.user_name ? ` · ${shown.user_name}` : ""}
                  </p>
                </div>

                <div className="divide-y rounded-md border">
                  {shown.lines.map((ln) => (
                    <div
                      key={ln.product_id}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {ln.product_nomi}
                        </div>
                        <div className="text-xs tabular-nums text-muted-foreground">
                          {Number(ln.miqdor)} {ln.birlik}
                          {ln.tannarx ? ` × ${fmtMoney(ln.tannarx)}` : ""}
                        </div>
                      </div>
                      <span className="font-medium tabular-nums">
                        {fmtMoney(ln.jami_tannarx)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      {t("chiqim.detail.sabab")}
                    </span>
                    <ReasonChip sabab={shown.sabab} t={t} />
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">
                      {t("chiqim.detail.qiymat")}
                    </span>
                    <Money tone={shown.jami_tannarx ? "neg" : "default"}>
                      {fmtMoney(shown.jami_tannarx)}
                    </Money>
                  </div>
                  {showSelector && (
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        {t("chiqim.detail.filial")}
                      </span>
                      <BranchLabel
                        name={shown.branch_name}
                        isActive={shown.branch_is_active}
                      />
                    </div>
                  )}
                  {shown.izoh && (
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        {t("chiqim.detail.izoh")}
                      </span>
                      <span className="text-right">{shown.izoh}</span>
                    </div>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  {t("chiqim.detail.immutableHint")}
                </p>
              </>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}

/* ─────────────────────────── Yangi chiqim formasi ─────────────────────────── */

type Line = { key: number; product: Product; miqdor: string };

let lineSeq = 1;

function ChiqimForm({
  initialProduct,
  onCancel,
  onSaved,
}: {
  /** Signallar tabidan oldindan qo'yilgan tovar (qotgan zaxira). */
  initialProduct: Product | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const { selectedBranchId, showSelector, branches } = useBranch();
  const [products, setProducts] = useState<Product[]>([]);
  // Oldindan qo'yilgan qator miqdori qo'lda qo'shilgan qator bilan AYNAN bir
  // xil qoidada (1 dona). Hujjat o'zgarmas, ya'ni "hammasini" deb oldindan
  // to'ldirish bir bosishda qaytarib bo'lmaydigan chiqim yozardi; ustiga
  // "barcha filiallar" ko'rinishida `joriy_ostatka` ustaxona bo'yicha
  // yig'indi bo'lib, bitta filialdagi zaxiradan oshib ketardi.
  const [lines, setLines] = useState<Line[]>(() =>
    initialProduct
      ? [
          {
            key: lineSeq++,
            product: initialProduct,
            miqdor: String(Math.min(1, Number(initialProduct.joriy_ostatka))),
          },
        ]
      : [],
  );
  const [sabab, setSabab] = useState<WriteOffReason>("singan");
  // Ko'p filialli ustaxonada filial ANIQ tanlanadi. Xarid/sotuvdan farqli
  // o'laroq bu yerda "jimgina asosiy filial" yaramaydi: singan tovar aynan
  // qaysi filialda turgani muhim, va topbar "Barcha filiallar"da bo'lsa
  // hujjat qayerga tushishi foydalanuvchiga ko'rinmasdi.
  const [branchId, setBranchId] = useState<string>(
    selectedBranchId != null
      ? String(selectedBranchId)
      : showSelector && branches.length > 0
        ? String(branches[0].id)
        : "",
  );
  const [izoh, setIzoh] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Bir marta generatsiya qilinadi: tarmoq qayta urinsa ham server dublikat
  // hujjat yozmaydi (backend `client_uuid` bo'yicha qisqa tutashuv qiladi).
  const [uuid] = useState(() => crypto.randomUUID());

  useEffect(() => {
    (async () => {
      try {
        setProducts(
          await api.products({
            faol: true,
            branch_id: selectedBranchId ?? undefined,
          }),
        );
      } catch {
        /* empty */
      }
    })();
  }, [selectedBranchId]);

  const usedIds = useMemo(
    () => new Set(lines.map((l) => l.product.id)),
    [lines],
  );
  // Yo'qotish qiymati tannarx bo'yicha — bu faqat oldindan ko'rsatkich.
  // Yakuniy `jami_tannarx`ni server yozuv ONIDAGI tannarx snapshot'idan
  // hisoblaydi, shuning uchun bu yerdagi son bilan bir xil bo'lishi
  // KAFOLATLANMAYDI (kartochka narxi orada o'zgargan bo'lishi mumkin).
  const total = useMemo(
    () =>
      lines.reduce(
        (s, l) => s + (Number(l.miqdor) || 0) * Number(l.product.tannarx),
        0,
      ),
    [lines],
  );

  function setLine(key: number, patch: Partial<Line>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function addProduct(p: Product) {
    setLines((ls) =>
      ls.some((l) => l.product.id === p.id)
        ? ls
        : [
            ...ls,
            {
              key: lineSeq++,
              product: p,
              miqdor: String(Math.min(1, Number(p.joriy_ostatka))),
            },
          ],
    );
  }

  const overStock = lines.some(
    (l) => (Number(l.miqdor) || 0) > Number(l.product.joriy_ostatka),
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (lines.length === 0) {
      setErr(t("chiqim.form.atLeastOne"));
      return;
    }
    if (overStock) {
      setErr(t("chiqim.form.overStock"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const wo = await api.createWriteOff({
        sabab,
        izoh: izoh.trim() || null,
        // Filial ANIQ uzatiladi — server uni tekshiradi (begona → 404), jimgina
        // boshqasiga almashtirmaydi. Yagona/filialsiz ustaxonada `undefined`.
        branch_id: branchId ? Number(branchId) : undefined,
        client_uuid: uuid,
        lines: lines.map((l) => ({
          product_id: l.product.id,
          miqdor: l.miqdor || "0",
        })),
      });
      (wo.ogohlantirishlar || []).forEach((w) => toast.warning(w));
      toast.success(t("chiqim.form.saved"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex max-h-[85vh] w-full flex-col">
      {/* 1. Qotirilgan header: sarlavha + tovar qidiruvi */}
      <div className="flex-none space-y-3 border-b p-4 sm:p-5">
        <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
          {t("chiqim.form.title")}
        </h2>
        <ProductCombobox
          products={products}
          excludeIds={usedIds}
          onPick={addProduct}
          unavailable={(p) => Number(p.joriy_ostatka) <= 0}
          disabled={busy}
        />
      </div>

      {/* 2. Yagona scroll zonasi: tovarlar jadvali */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-[120px]" />
            <col className="w-[140px]" />
            <col className="w-[40px]" />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
            <tr className="border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 text-left">{t("chiqim.form.tovar")}</th>
              <th className="px-2 py-2 text-center">
                {t("chiqim.form.miqdor")}
              </th>
              <th className="px-3 py-2 text-right">
                {t("chiqim.form.qiymat")}
              </th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-10 text-center text-sm text-muted-foreground"
                >
                  <PackageSearch className="mx-auto mb-2 size-6 opacity-60" />
                  {t("chiqim.form.emptyHint")}
                </td>
              </tr>
            ) : (
              lines.map((l) => {
                const avail = Number(l.product.joriy_ostatka);
                const over = (Number(l.miqdor) || 0) > avail;
                return (
                  <tr key={l.key} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <div className="truncate font-medium" title={l.product.nomi}>
                        {l.product.nomi}
                      </div>
                      <div
                        className={
                          over
                            ? "truncate text-xs text-destructive"
                            : "truncate text-xs text-muted-foreground"
                        }
                      >
                        {avail} {l.product.birlik}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center align-top">
                      <QtyStepper
                        value={l.miqdor}
                        onChange={(v) => setLine(l.key, { miqdor: v })}
                        disabled={busy}
                        max={avail}
                      />
                    </td>
                    <td className="px-3 py-2 text-right align-top font-semibold tabular-nums">
                      {fmtMoney(
                        (Number(l.miqdor) || 0) * Number(l.product.tannarx),
                      )}
                    </td>
                    <td className="px-2 py-2 text-center align-top">
                      <button
                        type="button"
                        onClick={() =>
                          setLines((ls) => ls.filter((x) => x.key !== l.key))
                        }
                        disabled={busy}
                        className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                        aria-label={t("shared.delete")}
                      >
                        <X className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {err && (
          <div className="m-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}
      </div>

      {/* 3. Qotirilgan footer: sabab (majburiy) + izoh + jami + tugmalar */}
      <div className="flex-none space-y-3 rounded-b-lg border-t bg-muted/30 p-4 sm:p-5">
        <div className={showSelector ? "grid grid-cols-2 gap-3" : "space-y-1.5"}>
          <div className="space-y-1.5">
            <Label htmlFor="wo-form-sabab">{t("chiqim.form.sabab")}</Label>
            <Select
              id="wo-form-sabab"
              value={sabab}
              onChange={(e) => setSabab(e.target.value as WriteOffReason)}
              disabled={busy}
            >
              {WRITE_OFF_REASONS.map((s) => (
                <option key={s} value={s}>
                  {t(`chiqim.sabab.${s}`)}
                </option>
              ))}
            </Select>
          </div>
          {showSelector && (
            <div className="space-y-1.5">
              <Label htmlFor="wo-form-branch">{t("chiqim.form.filial")}</Label>
              <Select
                id="wo-form-branch"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                disabled={busy}
              >
                {branches.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
        <Input
          value={izoh}
          onChange={(e) => setIzoh(e.target.value)}
          disabled={busy}
          placeholder={t("chiqim.form.izohPlaceholder")}
        />

        <div className="flex items-end justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {t("chiqim.form.noDebtHint")}
          </p>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("chiqim.form.jamiQiymat")}
            </div>
            <div className="text-2xl font-bold tabular-nums">
              {fmtMoney(total)}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-none"
          >
            {t("sotuvlar.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={busy || lines.length === 0 || overStock}
            className="flex-1"
          >
            {busy ? t("chiqim.form.submitting") : t("chiqim.form.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
}
